import {
  useCreateDiscipline,
  useGetDisciplinesPaginated,
  useRemoveDiscipline,
  useUpdateDiscipline,
} from '@/api/hooks/useDiscipline';
import { DisciplineOutput, DisciplineType } from '@/api/gql/graphql';
import { Header } from '@/components/Layout/Header/Header';
import { Modal } from '@/components/Layout';
import { useToast } from '@/hooks/useToast';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { useState } from 'react';

const PAGE_SIZE = 20;

const TYPE_LABELS: Record<DisciplineType, string> = {
  [DisciplineType.School]: 'Scolaire',
  [DisciplineType.University]: 'Université',
  [DisciplineType.Professional]: 'Professionnel',
  [DisciplineType.Other]: 'Autre',
};

const TYPE_OPTIONS = Object.values(DisciplineType);

const emptyForm = {
  name: '',
  description: '',
  types: [] as DisciplineType[],
};

const inputClass =
  'bg-[#2A2A2A] border border-[#8080803d] rounded px-3 py-2 text-white text-sm w-full outline-none focus:border-primary';
const labelClass = 'flex flex-col gap-1 text-sm font-semibold text-[#939393]';

const TypePill = ({ type }: { type: DisciplineType }) => {
  const tones: Record<DisciplineType, string> = {
    [DisciplineType.School]: 'bg-[#1A2A3A] text-[#60A5FA]',
    [DisciplineType.University]: 'bg-[#2A1A3A] text-[#C084FC]',
    [DisciplineType.Professional]: 'bg-[#1E3A24] text-[#4ADE80]',
    [DisciplineType.Other]: 'bg-[#3A2E12] text-[#FEC200]',
  };
  return (
    <span
      className={`px-2 py-1 rounded text-xs font-semibold ${tones[type]}`}
    >
      {TYPE_LABELS[type]}
    </span>
  );
};

// Types effectifs d'une discipline : `types` fait foi, repli sur `type`
// legacy pour les documents pas encore migrés.
const disciplineTypes = (row: DisciplineOutput): DisciplineType[] =>
  row.types?.length ? row.types : row.type ? [row.type] : [];

export const Disciplines = () => {
  const { showToast } = useToast();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<DisciplineType | ''>('');

  const { data, isLoading } = useGetDisciplinesPaginated({
    page,
    limit: PAGE_SIZE,
    ...(search.trim() && { search: search.trim() }),
    ...(typeFilter && { type: typeFilter }),
  });

  const { mutateAsync: createDiscipline, isPending: isCreating } =
    useCreateDiscipline();
  const { mutateAsync: updateDiscipline, isPending: isUpdating } =
    useUpdateDiscipline();
  const { mutateAsync: removeDiscipline, isPending: isRemoving } =
    useRemoveDiscipline();

  const [form, setForm] = useState(emptyForm);
  const [editedId, setEditedId] = useState<string | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [toDelete, setToDelete] = useState<DisciplineOutput | null>(null);

  const openCreate = () => {
    setEditedId(null);
    setForm(emptyForm);
    setIsFormOpen(true);
  };

  const openEdit = (row: DisciplineOutput) => {
    setEditedId(row._id);
    setForm({
      name: row.name,
      description: row.description ?? '',
      types: disciplineTypes(row),
    });
    setIsFormOpen(true);
  };

  const toggleFormType = (type: DisciplineType) => {
    setForm((prev) => ({
      ...prev,
      types: prev.types.includes(type)
        ? prev.types.filter((current) => current !== type)
        : [...prev.types, type],
    }));
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      showToast('Le nom de la discipline est requis.', 'error');
      return;
    }
    if (!form.types.length) {
      showToast('Sélectionnez au moins une catégorie.', 'error');
      return;
    }

    const input = {
      name: form.name.trim(),
      description: form.description.trim(),
      types: form.types,
    };

    try {
      if (editedId) {
        await updateDiscipline({ id: editedId, input });
        showToast('Discipline mise à jour.', 'success');
      } else {
        await createDiscipline(input);
        showToast('Discipline créée.', 'success');
      }
      setIsFormOpen(false);
    } catch (error) {
      showToast((error as Error).message, 'error');
    }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await removeDiscipline(toDelete._id);
      showToast('Discipline supprimée.', 'success');
      setToDelete(null);
    } catch (error) {
      showToast((error as Error).message, 'error');
    }
  };

  const headerCol = 'text-primary bg-[#131311] text-sm font-bold';
  const bodyCol = 'text-white bg-[#131311] border-b border-[#8080803d] text-sm';

  return (
    <>
      <Header title="Disciplines" goBack />

      <div className="px-6 py-4">
        <div className="flex justify-between items-center bg-[#232323] py-4 px-7 rounded-[8px] gap-4 flex-wrap">
          <p className="text-white text-lg font-bold">Matières</p>
          <div className="flex items-center gap-3 flex-wrap">
            <input
              className={`${inputClass} w-[220px]`}
              placeholder="Rechercher…"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
            />
            <select
              className={`${inputClass} w-[180px]`}
              value={typeFilter}
              onChange={(event) => {
                setTypeFilter(event.target.value as DisciplineType | '');
                setPage(1);
              }}
            >
              <option value="">Toutes les catégories</option>
              {TYPE_OPTIONS.map((type) => (
                <option key={type} value={type}>
                  {TYPE_LABELS[type]}
                </option>
              ))}
            </select>
            <button
              onClick={openCreate}
              className="bg-primary text-black font-bold text-sm px-5 h-[41px] rounded flex items-center gap-2"
            >
              <i className="pi pi-plus" /> Nouvelle discipline
            </button>
          </div>
        </div>

        <div className="bg-[#131311] p-4 mt-4 rounded-[8px]">
          <DataTable
            value={data?.data ?? []}
            loading={isLoading}
            emptyMessage="Aucune discipline."
            lazy
            paginator
            rows={PAGE_SIZE}
            totalRecords={data?.total ?? 0}
            first={(page - 1) * PAGE_SIZE}
            onPage={(event) => setPage((event.page ?? 0) + 1)}
          >
            <Column
              field="name"
              header="Nom"
              headerClassName={headerCol}
              bodyClassName={bodyCol}
            />
            <Column
              header="Catégories"
              headerClassName={headerCol}
              bodyClassName={bodyCol}
              body={(row: DisciplineOutput) => (
                <div className="flex gap-2 flex-wrap">
                  {disciplineTypes(row).map((type) => (
                    <TypePill key={type} type={type} />
                  ))}
                </div>
              )}
            />
            <Column
              header="Description"
              headerClassName={headerCol}
              bodyClassName={bodyCol}
              body={(row: DisciplineOutput) => (
                <span className="text-[#939393]">
                  {row.description || '—'}
                </span>
              )}
            />
            <Column
              header="Actions"
              headerClassName={headerCol}
              bodyClassName={bodyCol}
              body={(row: DisciplineOutput) => (
                <div className="flex gap-3">
                  <button
                    title="Modifier"
                    onClick={() => openEdit(row)}
                    className="text-primary"
                  >
                    <i className="pi pi-pencil" />
                  </button>
                  <button
                    title="Supprimer"
                    onClick={() => setToDelete(row)}
                    className="text-[#F87171]"
                  >
                    <i className="pi pi-trash" />
                  </button>
                </div>
              )}
            />
          </DataTable>
        </div>
      </div>

      {/* Création / édition */}
      <Modal
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        headerStyle="bg-carbon h-[36px]"
        modalStyle="bg-carbon"
      >
        <div className="flex flex-col gap-4 w-[460px] max-w-full p-2">
          <h3 className="text-white text-lg font-bold">
            {editedId ? 'Modifier la discipline' : 'Nouvelle discipline'}
          </h3>

          <label className={labelClass}>
            Nom
            <input
              className={inputClass}
              value={form.name}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, name: event.target.value }))
              }
              placeholder="Mathématiques"
            />
          </label>

          <label className={labelClass}>
            Description
            <textarea
              className={`${inputClass} min-h-[80px] resize-none`}
              value={form.description}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  description: event.target.value,
                }))
              }
              placeholder="Description de la discipline…"
            />
          </label>

          <div className={labelClass}>
            Catégories
            <div className="flex gap-2 text-sm mt-1">
              {TYPE_OPTIONS.map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => toggleFormType(type)}
                  className={`flex-1 py-2 rounded border ${
                    form.types.includes(type)
                      ? 'border-primary text-primary font-semibold'
                      : 'border-[#8080803d] text-[#939393]'
                  }`}
                >
                  {TYPE_LABELS[type]}
                </button>
              ))}
            </div>
            <span className="text-xs font-normal text-[#6b6b6b]">
              Une discipline peut appartenir à plusieurs catégories.
            </span>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              className="text-[#939393] text-sm"
              onClick={() => setIsFormOpen(false)}
            >
              Annuler
            </button>
            <button
              className="bg-primary text-black font-bold text-sm px-5 py-2 rounded disabled:opacity-50"
              disabled={isCreating || isUpdating}
              onClick={handleSubmit}
            >
              {isCreating || isUpdating
                ? 'Enregistrement…'
                : editedId
                  ? 'Enregistrer'
                  : 'Créer'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Confirmation de suppression */}
      <Modal
        isOpen={!!toDelete}
        onClose={() => setToDelete(null)}
        headerStyle="bg-carbon h-[36px]"
        modalStyle="bg-carbon"
      >
        <div className="flex flex-col gap-4 w-[400px] max-w-full p-2">
          <h3 className="text-white text-lg font-bold">
            Supprimer la discipline
          </h3>
          <p className="text-[#939393] text-sm">
            Supprimer «{toDelete?.name}» ? Elle ne sera plus proposée à la
            création de classe.
          </p>
          <div className="flex justify-end gap-3 pt-2">
            <button
              className="text-[#939393] text-sm"
              onClick={() => setToDelete(null)}
            >
              Annuler
            </button>
            <button
              className="bg-[#BD292F] text-white font-bold text-sm px-5 py-2 rounded disabled:opacity-50"
              disabled={isRemoving}
              onClick={handleDelete}
            >
              {isRemoving ? 'Suppression…' : 'Supprimer'}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};
